import { BaseService } from "./BaseService";
import {
    PublicClientApplication,
    InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { notificationRequest } from "@/config/msal-config";

export interface Notification {
    messageId: string | number;
    messageHeader: string;
    messageContent: string;
    createdOn: string;
    readIndicator: boolean;
    user: {
        name: string;
        avatar: string;
    };
    customData?: Record<string, unknown> | string; // Can be object (from SignalR) or string (from DB) - will be parsed
}

export class NotificationError extends Error {
    constructor(
        message: string,
        public readonly requiresAdminConsent: boolean = false
    ) {
        super(message);
        this.name = "NotificationError";
    }
}

const notificationHostPath = process.env.NEXT_PUBLIC_NOTIFICATION_ENDPOINT_URL;

export async function getNotificationAPIToken(
    msalInstance: PublicClientApplication
): Promise<string> {
    if (!msalInstance) {
        throw new NotificationError("MSAL not initialized");
    }

    let account = msalInstance.getActiveAccount();
    if (!account) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            account = accounts[0];
            msalInstance.setActiveAccount(account);
        }
    }

    if (!account) {
        throw new NotificationError("No active account found. Please log in.");
    }

    const request = {
        ...notificationRequest,
        account,
    };

    try {
        const accessTokenResponse =
            await msalInstance.acquireTokenSilent(request);
        return accessTokenResponse.accessToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            try {
                const interactiveResponse =
                    await msalInstance.acquireTokenPopup(request);
                return interactiveResponse.accessToken;
            } catch {
                throw new NotificationError(
                    "Could not acquire token interactively."
                );
            }
        }
        const errorMessage =
            error instanceof Error ? error.message : String(error);
        throw new NotificationError(
            `Failed to acquire notification token: ${errorMessage}`
        );
    }
}

export async function fetchNotifications(
    applicationName: string,
    userEmail: string,
    msalInstance: PublicClientApplication
): Promise<Notification[]> {
    try {
        const token = await getNotificationAPIToken(msalInstance);
        const res = await fetch(
            `${notificationHostPath}/notification/GetNotifications/${applicationName}/${userEmail}`,
            {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
            }
        );
        if (!res.ok) {
            if (res.status === 403) {
                throw new NotificationError(
                    "You don't have permission to access notifications. Please contact your administrator.",
                    true
                );
            }
            throw new NotificationError("Failed to fetch notifications");
        }
        const notifications: Notification[] = await res.json();

        // Parse customData if it's a JSON string (from database storage)
        return notifications.map((notification) => ({
            ...notification,
            customData:
                typeof notification.customData === "string"
                    ? (() => {
                          try {
                              return JSON.parse(notification.customData);
                          } catch {
                              return notification.customData; // Return as-is if parsing fails
                          }
                      })()
                    : notification.customData,
        }));
    } catch (error) {
        if (error instanceof NotificationError) {
            throw error;
        }
        throw new NotificationError(
            "An error occurred while fetching notifications"
        );
    }
}

export async function markAllAsRead(
    userEmail: string,
    msalInstance: PublicClientApplication
): Promise<void> {
    const token = await getNotificationAPIToken(msalInstance);
    const res = await fetch(
        `${notificationHostPath}/notification/updateAllNotification/${userEmail}`,
        {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        }
    );
    if (!res.ok) throw new Error("Failed to mark all as read");
}

export async function markNotificationAsRead(
    messageId: string,
    userEmail: string,
    msalInstance: PublicClientApplication
): Promise<void> {
    const token = await getNotificationAPIToken(msalInstance);
    const res = await fetch(
        `${notificationHostPath}/notification/updateNotification/${messageId}?userEmail=${encodeURIComponent(userEmail)}`,
        {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        }
    );
    if (!res.ok) throw new Error("Failed to mark notification as read");
}

export interface NotificationModel {
    applicationName?: string;
    type?: string;
    messageId: number;
    messageHeader?: string;
    messageContent?: string;
    recipientEmailAddress?: string;
    createdOn: string; // ISO string
    createdBy?: string;
    readIndicator: boolean;
}

class NotificationServiceClass extends BaseService {
    protected get baseUrl(): string {
        return `${process.env.NEXT_PUBLIC_NOTIFICATION_ENDPOINT_URL}/api/notification/`;
    }

    async getNotifications(): Promise<NotificationModel[]> {
        return this.get<NotificationModel[]>("GetOrganizationList");
    }

    async updateNotification(
        data: NotificationModel
    ): Promise<NotificationModel> {
        return this.post("CreateNotification", data);
    }
}

export const notificationService = new NotificationServiceClass();
